/**
 * /trust/sandbox proof surface for Repancy.
 *
 * Six safe Microsoft Graph operations a signed-in customer can run against
 * their OWN tenant to verify the Repancy integration is real. The wrapper
 * enforces:
 *   1. Path allow-list — any Graph URL outside the six allow-listed paths
 *      throws `SandboxOutOfScopeError`.
 *   2. Scratch-file-only writes — only `repancy-sandbox-{userId}-{ts}.md`
 *      in the user's OneDrive root. Anywhere else => `SandboxOutOfScopeError`.
 *   3. Audit — every Run writes one row through the injected `AuditWriter`.
 *      The writer decides live vs demo and where rows land; sandbox.ts
 *      stays a leaf module with no Convex/React imports.
 *
 * Demo mode: the panel passes `isDemo: true` and a `DemoFixtureProvider`.
 * The sandbox short-circuits to fixtures and still writes the audit row,
 * so /trust/audit shows real coverage of what the demo user did.
 *
 * Memory: project_repancy_demo_in_convex.md, MILESTONE-A.md §A.7
 */

import { ResponseType } from '@microsoft/microsoft-graph-client';
import { getGraphClient } from './graphClient';
import { acquireGraphToken } from './msalClient';

// ---------------------------------------------------------------------------
// Types — exported for adapters in sandboxWiring.ts and tests
// ---------------------------------------------------------------------------

export type SandboxEvent =
  | 'sandbox-me'
  | 'sandbox-groups'
  | 'sandbox-sites'
  | 'sandbox-permissions'
  | 'sandbox-dryrun-createlink'
  | 'sandbox-roundtrip';

export interface SandboxSummary {
  upnDomain?: string;
  groupCount?: number;
  roleCount?: number;
  siteCount?: number;
  searchTerm?: string;
  permissionCount?: number;
  itemIdSuffix?: string;
  linkType?: string;
  linkScope?: string;
  stepsOk?: number;
  totalSteps?: number;
  fileSize?: number;
  failStepIndex?: number;
  failReason?: string;
}

export interface AuditWriter {
  write(event: SandboxEvent, summary: SandboxSummary): Promise<void>;
}

export interface MeResponse {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
  jobTitle?: string;
  officeLocation?: string;
}

export interface GroupsResponse {
  value: Array<{ id: string; displayName?: string; '@odata.type'?: string }>;
}

export interface SitesResponse {
  value: Array<{ id?: string; displayName?: string; webUrl?: string }>;
}

export interface PermissionsResponse {
  value: Array<{
    id: string;
    roles?: string[];
    grantedTo?: { user?: { displayName?: string; email?: string } };
    grantedToIdentities?: Array<{ user?: { displayName?: string; email?: string } }>;
    link?: { type?: string; scope?: string; webUrl?: string };
  }>;
}

export interface RoundTripStep {
  label: string;
  status: 'pending' | 'ok' | 'fail';
  detail?: string;
}

export interface RoundTripResult {
  steps: RoundTripStep[];
  success: boolean;
  fileSize?: number;
}

export interface DryRunCreateLinkResult {
  target: string;
  body: { type: string; scope: string };
}

/** Fixture provider for demo mode. Site list ideally comes from Convex; other fixtures are inline. */
export interface DemoFixtureProvider {
  getMe(): Promise<MeResponse>;
  getMyGroups(): Promise<GroupsResponse>;
  listSites(term: string): Promise<SitesResponse>;
  getItemPermissions(driveId: string, itemId: string): Promise<PermissionsResponse>;
  runOneDriveRoundTrip(userId: string): Promise<RoundTripResult>;
}

export interface SandboxDeps {
  audit: AuditWriter;
  fixtures?: DemoFixtureProvider;
  isDemo: boolean;
}

export interface SandboxApi {
  getMe(): Promise<MeResponse>;
  getMyGroups(): Promise<GroupsResponse>;
  searchSites(term: string): Promise<SitesResponse>;
  getItemPermissions(driveId: string, itemId: string): Promise<PermissionsResponse>;
  dryRunCreateLink(opts: DryRunCreateLinkOpts): Promise<DryRunCreateLinkResult>;
  runOneDriveRoundTrip(opts: RoundTripOpts): Promise<RoundTripResult>;
}

export interface DryRunCreateLinkOpts {
  driveId: string;
  itemId: string;
  type?: 'view' | 'edit' | 'embed';
  scope?: 'anonymous' | 'organization' | 'users';
}

export interface RoundTripOpts {
  userId: string;
  onStep?: (s: RoundTripStep, all: RoundTripStep[]) => void;
}

// ---------------------------------------------------------------------------
// Errors + path validators (unchanged from A.6)
// ---------------------------------------------------------------------------

export class SandboxOutOfScopeError extends Error {
  constructor(message: string, public attemptedPath: string) {
    super(message);
    this.name = 'SandboxOutOfScopeError';
  }
}

/**
 * Returns the scratch file name we permit writes to. Stable per user-session
 * so the same user calling round-trip twice in quick succession writes to a
 * single file (latest call wins, then deletes).
 */
export function scratchFileName(userId: string): string {
  const ts = Date.now();
  return `repancy-sandbox-${userId}-${ts}.md`;
}

function assertAllowedRead(path: string): void {
  const allowed = [
    /^\/me$/,
    /^\/me\/memberOf(\?.*)?$/,
    /^\/sites(\?search=[^&]+)?$/,
    /^\/sites\/[^/]+$/,
    /^\/drives\/[^/]+\/items\/[^/]+\/permissions(\?.*)?$/,
    /^\/me\/drive\/root\/children(\?.*)?$/,
  ];
  if (!allowed.some((r) => r.test(path))) {
    throw new SandboxOutOfScopeError(
      `Path "${path}" is not in the sandbox allow-list. Sandbox only permits /me, /me/memberOf, /sites?search=..., /sites/{id}, /drives/{id}/items/{id}/permissions, /me/drive/root/children.`,
      path,
    );
  }
}
function assertAllowedScratchPath(path: string): void {
  const scratchInName = /^\/me\/drive\/root:\/repancy-sandbox-[^/]+\.md(?::\/.*)?$/;
  const byItemId = /^\/me\/drive\/items\/[^/]+(\/content)?$/;
  if (!scratchInName.test(path) && !byItemId.test(path)) {
    throw new SandboxOutOfScopeError(
      `Path "${path}" is not in the sandbox scratch-file allow-list.`,
      path,
    );
  }
}

async function sandboxGet<T>(path: string): Promise<T> {
  assertAllowedRead(path);
  await acquireGraphToken();
  const client = getGraphClient();
  return (await client.api(path).get()) as T;
}

// ---------------------------------------------------------------------------
// Internal helpers — extract redacted summary primitives from a response
// ---------------------------------------------------------------------------

function extractUpnDomain(me: MeResponse): string | undefined {
  const upn = me.userPrincipalName ?? me.mail;
  if (!upn) return undefined;
  const at = upn.lastIndexOf('@');
  return at >= 0 ? upn.slice(at + 1) : undefined;
}

function countGroupsAndRoles(groups: GroupsResponse): { groupCount: number; roleCount: number } {
  let groupCount = 0;
  let roleCount = 0;
  for (const g of groups.value) {
    if (g['@odata.type']?.includes('directoryRole')) roleCount++;
    else groupCount++;
  }
  return { groupCount, roleCount };
}

function lastChars(s: string, n: number): string {
  return s.length > n ? s.slice(-n) : s;
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------
// Factory — the only public entry point besides errors/helpers above
// ---------------------------------------------------------------------------

export function makeSandbox(deps: SandboxDeps): SandboxApi {
  const { audit, fixtures, isDemo } = deps;
  if (isDemo && !fixtures) {
    throw new Error('makeSandbox: fixtures provider required when isDemo=true');
  }

  // Audit dispatch helper. Always called once per public method; never throws.
  async function record(event: SandboxEvent, summary: SandboxSummary): Promise<void> {
    try {
      await audit.write(event, summary);
    } catch {
      // Audit failures are non-fatal — the sandbox is a customer-facing trust
      // surface and a Convex hiccup should not break the panel.
    }
  }

  async function getMe(): Promise<MeResponse> {
    try {
      const me = isDemo ? await fixtures!.getMe() : await sandboxGet<MeResponse>('/me');
      await record('sandbox-me', { upnDomain: extractUpnDomain(me) });
      return me;
    } catch (err) {
      await record('sandbox-me', { failReason: errMessage(err) });
      throw err;
    }
  }

  async function getMyGroups(): Promise<GroupsResponse> {
    try {
      const groups = isDemo
        ? await fixtures!.getMyGroups()
        : await sandboxGet<GroupsResponse>('/me/memberOf?$top=20');
      const counts = countGroupsAndRoles(groups);
      await record('sandbox-groups', counts);
      return groups;
    } catch (err) {
      await record('sandbox-groups', { failReason: errMessage(err) });
      throw err;
    }
  }

  async function searchSites(term: string): Promise<SitesResponse> {
    const t = (term ?? '').trim();
    if (t.length < 1) {
      // Mirror the live guard in both modes — same DX, audit the attempt.
      await record('sandbox-sites', {
        siteCount: 0,
        searchTerm: '',
        failReason: 'empty search term',
      });
      throw new SandboxOutOfScopeError(
        'Search term required — Graph rejects empty/space/asterisk searches.',
        '/sites?search=',
      );
    }
    try {
      const sites = isDemo
        ? await fixtures!.listSites(t)
        : await sandboxGet<SitesResponse>(`/sites?search=${encodeURIComponent(t)}`);
      await record('sandbox-sites', { siteCount: sites.value.length, searchTerm: t });
      return sites;
    } catch (err) {
      await record('sandbox-sites', { searchTerm: t, failReason: errMessage(err) });
      throw err;
    }
  }

  async function getItemPermissions(driveId: string, itemId: string): Promise<PermissionsResponse> {
    try {
      const perms = isDemo
        ? await fixtures!.getItemPermissions(driveId, itemId)
        : await sandboxGet<PermissionsResponse>(
            `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/permissions`,
          );
      await record('sandbox-permissions', {
        permissionCount: perms.value.length,
        itemIdSuffix: lastChars(itemId, 8),
      });
      return perms;
    } catch (err) {
      await record('sandbox-permissions', {
        itemIdSuffix: lastChars(itemId, 8),
        failReason: errMessage(err),
      });
      throw err;
    }
  }

  async function dryRunCreateLink(opts: DryRunCreateLinkOpts): Promise<DryRunCreateLinkResult> {
    const body = {
      type: opts.type ?? 'view',
      scope: opts.scope ?? 'organization',
    };
    const target = `/drives/${opts.driveId}/items/${opts.itemId}/createLink`;
    await record('sandbox-dryrun-createlink', {
      linkType: body.type,
      linkScope: body.scope,
      itemIdSuffix: lastChars(opts.itemId, 8),
    });
    return { target, body };
  }

  async function runOneDriveRoundTrip(opts: RoundTripOpts): Promise<RoundTripResult> {
    if (isDemo) {
      const demo = await fixtures!.runOneDriveRoundTrip(opts.userId);
      // Emit each step so the panel can render progress.
      for (const s of demo.steps) opts.onStep?.(s, demo.steps);
      await record('sandbox-roundtrip', {
        stepsOk: demo.steps.filter((s) => s.status === 'ok').length,
        totalSteps: demo.steps.length,
        fileSize: demo.fileSize ?? 0,
      });
      return demo;
    }

    const steps: RoundTripStep[] = [
      { label: 'Acquire MSAL access token', status: 'pending' },
      { label: 'PUT scratch file content', status: 'pending' },
      { label: 'GET file metadata', status: 'pending' },
      { label: 'GET file content back', status: 'pending' },
      { label: 'DELETE scratch file', status: 'pending' },
    ];
    const emit = () => {
      const last = steps[steps.length - 1];
      if (last) opts.onStep?.(last, [...steps]);
    };
    const mark = (i: number, status: 'ok' | 'fail', detail?: string) => {
      const s = steps[i];
      if (s) {
        s.status = status;
        if (detail !== undefined) s.detail = detail;
      }
      emit();
    };

    let fileSize: number | undefined;
    try {
      // 1. Token
      await acquireGraphToken();
      mark(0, 'ok');

      // 2. PUT
      const name = scratchFileName(opts.userId);
      const putPath = `/me/drive/root:/${encodeURIComponent(name)}:/content`;
      assertAllowedScratchPath(putPath);
      const content = `Repancy sandbox round-trip at ${new Date().toISOString()}\n`;
      const client = getGraphClient();
      const created = (await client
        .api(putPath)
        .header('Content-Type', 'text/plain')
        .put(content)) as { id: string; name: string };
      mark(1, 'ok', `id=${created.id.slice(0, 12)}…`);

      // 3. GET metadata
      const getPath = `/me/drive/items/${created.id}`;
      assertAllowedScratchPath(getPath);
      const meta = (await client.api(getPath).get()) as { name: string; size: number };
      fileSize = meta.size;
      mark(2, 'ok', `${meta.name}, ${meta.size}b`);

      // 4. GET content
      const contentPath = `/me/drive/items/${created.id}/content`;
      assertAllowedScratchPath(contentPath);
      const bytes = await client.api(contentPath).responseType(ResponseType.TEXT).get();
      const text = typeof bytes === 'string' ? bytes : String(bytes);
      const matches = text.includes('Repancy sandbox round-trip');
      mark(3, matches ? 'ok' : 'fail', matches ? 'content matches' : 'content mismatch');

      // 5. DELETE
      assertAllowedScratchPath(getPath);
      await client.api(getPath).delete();
      mark(4, 'ok', 'cleanup OK');

      const stepsOk = steps.filter((s) => s.status === 'ok').length;
      const success = stepsOk === steps.length;
      if (success) {
        await record('sandbox-roundtrip', { stepsOk, totalSteps: steps.length, fileSize: fileSize ?? 0 });
      } else {
        const failIdx = steps.findIndex((s) => s.status === 'fail');
        await record('sandbox-roundtrip', {
          failStepIndex: failIdx + 1,
          totalSteps: steps.length,
          failReason: steps[failIdx]?.detail ?? 'unknown',
        });
      }
      return { steps, success, fileSize };
    } catch (err) {
      const idx = steps.findIndex((s) => s.status === 'pending');
      const message = errMessage(err);
      if (idx >= 0) mark(idx, 'fail', message);
      await record('sandbox-roundtrip', {
        failStepIndex: (idx >= 0 ? idx : 0) + 1,
        totalSteps: steps.length,
        failReason: message,
      });
      return { steps, success: false, fileSize };
    }
  }

  return {
    getMe,
    getMyGroups,
    searchSites,
    getItemPermissions,
    dryRunCreateLink,
    runOneDriveRoundTrip,
  };
}
